import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  Output
} from '@angular/core';
import { BaseButtonSizeEnum } from '../base-button/base-button-size.enum';
import { BaseButtonColourEnum } from '../base-button/base-button-colour.enum';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { maxFileSizeValidator } from '../form-components/file-uploader/file-upload-field/max-file-size-validator';
import { fileFormattingErrorValidator } from '../form-components/file-uploader/file-upload-field/file-formatting-error-validator';
import { Subscribable } from '../../../../models/base/subscribable';
import { SPP_NEW_MEMBER_TEMPLATE_LINK } from '../../../../consts/consts';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BatchAddMembersFilePreviewModalComponent } from '../batch-add-members-file-preview-modal/batch-add-members-file-preview-modal.component';
import { ModalUtils } from '../../../../utils/modal-utils';
import { ConfirmationOptions } from '../../../../models/shared/stylesheet/confirmation-options';
import { ModalConfirmation } from '../../../../modals/modal-confirmation';
import { MembersFileRecord } from '../batch-add-members-file-preview-modal/interfaces/members-file-record';

@Component({
  selector: 'app-batch-add-member-file-upload',
  templateUrl: './batch-add-member-file-upload.component.html',
  styleUrls: ['./batch-add-member-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchAddMemberFileUploadComponent extends Subscribable implements AfterViewInit {
  @Input() cardTitle: string = '';
  @Input() description: string = '';
  @Output() membersAdded = new EventEmitter<MembersFileRecord[]>();

  private ngbModal = inject(NgbModal);
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);

  private _hasFormattingError = new BehaviorSubject<boolean>(false);
  public hasFormattingError$ = this._hasFormattingError as Observable<boolean>;

  private _primaryText = new BehaviorSubject<string>($localize`Continue with Template`);
  public primaryText$ = this._primaryText as Observable<string>;

  private membersToAdd: MembersFileRecord[] = [];

  public formGroup = new FormGroup({
    file: new FormControl<File[]>([], {
      validators: [
        Validators.required,
        maxFileSizeValidator(3),
        fileFormattingErrorValidator(this.hasFormattingError$)
      ],
      nonNullable: true
    })
  });

  public openPreviewModal(f: File): void {
    const modalRef = this.ngbModal.open(
      BatchAddMembersFilePreviewModalComponent,
      ModalUtils.defaultExtraLargeModalOptions(this.injector)
    );
    modalRef.componentInstance.setFileToPreview(f);
    modalRef.result.then(res => {
      if (res?.records) {
        this.membersToAdd = res.records;
        if (res?.continue) this.submitForm();
      } else if (res?.closedWithErrors) {
        this._hasFormattingError.next(true);
        this._primaryText.next($localize`Discard Upload`);
      } else if (res?.discardUpload) {
        this.confirmRemoveFile();
      } else {
        this._hasFormattingError.next(false);
        this._primaryText.next($localize`Continue with Template`);
      }
    });
  }

  public onRemoveFile(): void {
    this.confirmRemoveFile();
  }

  private removeFile(): void {
    this._hasFormattingError.next(false);
    this.formGroup.controls.file?.reset();
    this.cdr.detectChanges();
  }

  private confirmRemoveFile(): void {
    const opts = new ConfirmationOptions();
    opts.title = $localize`Discard this Upload?`;
    opts.bodyText = $localize`Are you sure you want to discard this file?`;
    opts.continueText = $localize`Discard`;
    opts.cancelText = $localize`Cancel`;
    ModalConfirmation.open(this.ngbModal, this.injector, opts, confirm => {
      if (confirm) {
        this.removeFile();
      }
    });
  }

  public submitForm(): void {
    this.membersAdded.emit(this.membersToAdd);
  }

  public downloadTemplate(): void {
    window.open(SPP_NEW_MEMBER_TEMPLATE_LINK, '_blank');
  }

  public skipFileUpload(): void {
    this.membersAdded.emit([]);
  }

  public primaryClicked(): void {
    const fileCtrl = this.formGroup.controls.file;
    const selectedFiles = fileCtrl?.value ?? [];

    this.hasFormattingError$.once(hasError => {
      if (hasError && selectedFiles.length) {
        this.removeFile();
      } else {
        this.submitForm();
      }
    });
  }

  public ngAfterViewInit(): void {
    this.formGroup.valueChanges.subscribeWhileAlive({
      owner: this,
      next: formVal => {
        const file = formVal.file;
        if (file && file.length > 0) {
          this.openPreviewModal(file[0]);
        }
      }
    });

    this.hasFormattingError$.subscribeWhileAlive({
      owner: this,
      next: hasError => {
        const fileCtrl = this.formGroup.controls.file;
        if (!fileCtrl) return;

        if (hasError) {
          fileCtrl.setErrors({ ...(fileCtrl.errors ?? {}), formattingError: true });
        } else {
          if (fileCtrl.errors) {
            delete fileCtrl.errors.formattingError;
            fileCtrl.setErrors(Object.keys(fileCtrl.errors).length ? fileCtrl.errors : null);
          }
        }
        fileCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    });
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
  protected readonly BaseButtonColourEnum = BaseButtonColourEnum;
}
